import React from 'react';

export interface TabItem<T extends string = string> {
  id: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  count?: number;
  /** Kept for guided tours that anchor on tab buttons. */
  dataTour?: string;
  /** Kept for tests that find a tab by data-testid. */
  testId?: string;
}

interface TabsProps<T extends string> {
  tabs: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  'aria-label'?: string;
}

/** Horizontal tab bar. Replaces the dozen hand-built bars (Data, Activity,
 *  Admin, Settings, Human Actions…) that each picked their own color. */
export function Tabs<T extends string>({ tabs, value, onChange, ...rest }: TabsProps<T>) {
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const i = tabs.findIndex(t => t.id === value);
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    if (next) onChange(next.id);
  };
  return (
    <div className="ds-tabs" role="tablist" aria-label={rest['aria-label']} onKeyDown={onKeyDown}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          className="ds-tab"
          aria-selected={tab.id === value}
          tabIndex={tab.id === value ? 0 : -1}
          data-tour={tab.dataTour}
          data-testid={tab.testId}
          onClick={() => onChange(tab.id)}
        >
          {tab.icon}
          {tab.label}
          {tab.count !== undefined && <span className="ds-tab__count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

export default Tabs;
